import { InformationIcon, AlertIcon } from '@atgfr8/icons';
import { JSX } from 'solid-js';
import toast, { Toast } from 'solid-toast';

import {
  CustomToast,
  CustomToastWithHoverPause,
  InboxToast,
  MultiLineToast,
  WithTimerToast,
} from './CustomToasts';
import {
  bellNotificationsEnabled,
  toastNotificationPosition,
  toastNotificationsEnabled,
} from './notificationPreferences';
import { recordToastActivity } from './toastActivity';

export type OpenToastProps = {
  message: string;
  label?: string;
  position?: ToastPosition;
  type?: ToastType;
  title?: string;
  duration?: number;
  onClose?: () => void;
  href?: string;
  linkText?: string;
  /** When false, the message is a popup only and is not added to the activity clock. */
  recordActivity?: boolean;
  /** The border currently only applies to the `Caution` type. */
  style?: JSX.CSSProperties;
};

export enum ToastType {
  Success = 'Success',
  Error = 'Error',
  Info = 'Info',
  Loading = 'Loading',
  Custom = 'Custom',
  MultiLine = 'MultiLine',
  Inbox = 'Inbox',
  WithTimer = 'WithTimer',
  Caution = 'Caution',
}

export type ToastPosition =
  | 'top-right'
  | 'bottom-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'top-left';

const defaultToastOptions = {
  duration: 10000,
  style: { 'max-width': '700px' },
};

export const showToastPreview = (message: string) => {
  const duration = defaultToastOptions.duration;
  const position = toastNotificationPosition();
  toast.success(
    (t) => (
      <CustomToastWithHoverPause
        message={message}
        onClose={() => toast.dismiss(t.id)}
        t={t}
        duration={duration}
      />
    ),
    {
      position,
      duration,
      style: defaultToastOptions.style,
    },
  );
};

export const openToast = (opts: OpenToastProps) => {
  const sendToBell =
    opts.recordActivity !== false &&
    opts.type !== ToastType.Error &&
    bellNotificationsEnabled() &&
    opts.message.trim().length > 0;

  if (sendToBell) {
    recordToastActivity({
      type: opts.type ?? ToastType.Info,
      message: opts.message,
      title: opts.title,
      href: opts.href,
      linkText: opts.linkText,
    });
  }

  const showPopup =
    opts.type === ToastType.Error || toastNotificationsEnabled();
  if (!showPopup) {
    return;
  }

  const {
    type,
    duration = defaultToastOptions.duration,
    title,
    onClose,
  } = opts;
  const position = toastNotificationPosition();
  const message =
    opts.linkText && !opts.message.includes(opts.linkText)
      ? `${opts.message}\n${opts.linkText}`
      : opts.message;

  const onCloseInternal = (t: Toast) => {
    toast.dismiss(t.id);
    onClose?.();
  };

  switch (type) {
    case ToastType.Success:
      toast.success(
        (t) => (
          <CustomToastWithHoverPause
            message={message}
            onClose={() => onCloseInternal(t)}
            t={t}
            duration={duration}
            title={title}
          />
        ),
        {
          position,
          duration,
          style: defaultToastOptions.style,
        },
      );
      break;

    case ToastType.Error:
      toast.error(
        (t) => (
          <CustomToastWithHoverPause
            message={message}
            onClose={() => onCloseInternal(t)}
            t={t}
            duration={duration}
          />
        ),
        {
          duration,
          position,
          style: defaultToastOptions.style,
        },
      );
      break;
    case ToastType.Info:
      toast(
        (t) => (
          <CustomToastWithHoverPause
            message={message}
            onClose={() => onCloseInternal(t)}
            t={t}
            duration={duration}
          />
        ),
        {
          duration,
          icon: <InformationIcon style={{ color: '#2D9CDB' }} />,
          position,
          style: defaultToastOptions.style,
        },
      );
      break;
    case ToastType.Loading:
      toast.loading(message, {
        duration,
        position,
      });
      break;

    case ToastType.Caution:
      toast(
        (t) => (
          <CustomToastWithHoverPause
            message={message}
            onClose={() => onCloseInternal(t)}
            t={t}
            duration={duration}
          />
        ),
        {
          duration,
          icon: <AlertIcon style={{ color: '#F59D25' }} />,
          position,
          style: opts.style ?? defaultToastOptions.style,
        },
      );
      break;

    case ToastType.Custom:
      toast.custom(
        (t: Toast) => (
          <CustomToast
            message={message}
            onClose={() => onCloseInternal(t)}
            visible={t.visible}
          />
        ),
        {
          duration,
          position,
        },
      );
      break;

    case ToastType.MultiLine:
      toast.custom(() => <MultiLineToast message={message} />, {
        duration,
        position,
      });
      break;

    case ToastType.Inbox:
      toast.custom(
        (t) => (
          <InboxToast
            title={title ?? 'Inbox Message'}
            message={message}
            onClose={() => onCloseInternal(t)}
          />
        ),
        { duration, position },
      );
      break;

    case ToastType.WithTimer:
      toast.custom(
        (t) => (
          <WithTimerToast
            message={message}
            duration={duration}
            onClose={() => onCloseInternal(t)}
            visible={t.visible}
            id={t.id}
            title={title ?? 'Title'}
          />
        ),
        { duration, position },
      );
      break;

    default:
      toast(message, { duration, position }); // Default case for simple text toast
      break;
  }
};
